/* Theme custom file */
import { mediaQueryLarge } from '@theme/utilities';

/** Office-configured position is read from shop metafields; these mirror core-api's DEFAULT_BESTSELLERS_POSITION. */
const DEFAULT_POSITION = { site: 4, app: 8 };

/** App value that hides the block entirely (phones follow the App setting, hiding included). */
const NO_BESTSELLERS = 'no-bestsellers';

const mediaQueryDesktop = matchMedia('(min-width: 990px)');

/**
 * Smartphone by device type, not viewport width: a desktop in a narrow window still follows the
 * Site position (doc §3.3). iPad and Android tablets are NOT phones (they carry no "Mobile" token),
 * so they keep the Site position too.
 * @returns {boolean}
 */
function isPhone() {
  return /Android.+Mobile|iPhone|iPod|Windows Phone|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent);
}

/**
 * The position that applies here: the App metafield on phones, the Site metafield otherwise.
 * `no-bestsellers` passes through to hide the block; any empty or malformed value falls back to the
 * surface default; a parsed index is clamped to a minimum of 1 (edge case B: never above the hero).
 * @param {HTMLElement} grid
 * @returns {number | typeof NO_BESTSELLERS}
 */
function resolvePosition(grid) {
  const phone = isPhone();
  const raw = phone ? grid.dataset.bestsellersPositionApp : grid.dataset.bestsellersPositionSite;
  if (raw === NO_BESTSELLERS) return NO_BESTSELLERS;

  const parsed = Number.parseInt(raw ?? '', 10);
  if (!Number.isFinite(parsed)) return phone ? DEFAULT_POSITION.app : DEFAULT_POSITION.site;
  return Math.max(1, parsed);
}

/**
 * Lines the hero occupies above the grid. A row holds two sales from 750px and one below, so the
 * four-sale hero is two lines on desktop/tablet and four on the single-column mobile site; the
 * single banner is one line everywhere.
 * @param {string} heroLayout
 * @param {number} heroCount
 * @param {boolean} isLarge
 * @returns {number}
 */
function heroLines(heroLayout, heroCount, isLarge) {
  if (heroLayout === 'none' || heroCount <= 0) return 0;
  return isLarge ? Math.ceil(heroCount / 2) : heroCount;
}

/**
 * Finds where the block goes so that exactly `position` lines sit above it, counting the hero plus
 * the active sale cards in DOM order. From 750px a row holds two cards and a featured card spans the
 * whole row, so the block only lands on a row boundary (an even slot count); below 750px every card
 * is its own line. A position that lands within or above the hero pins the block right after it
 * (edge case B); one deeper than the grid pins it at the end of the home (edge case A).
 * @param {boolean[]} featured - whether each visible active card is a featured grid card
 * @param {{ heroLayout: string, heroCount: number, isLarge: boolean, position: number }} options
 * @returns {{ afterIndex: number, linesAbove: number, featureGridCount: number } | null}
 */
function computeInsertion(featured, { heroLayout, heroCount, isLarge, position }) {
  const hero = heroLines(heroLayout, heroCount, isLarge);
  const gridLinesTarget = position - hero;

  // Position falls within or above the hero: the block goes right after the hero, before the first
  // card. afterIndex -1 encodes "insert before cards[0]".
  if (gridLinesTarget <= 0) {
    return featured.length ? { afterIndex: -1, linesAbove: hero, featureGridCount: 0 } : null;
  }

  let featureGridCount = 0;

  if (isLarge) {
    const targetSlots = gridLinesTarget * 2;
    let slots = 0;
    for (const [index, isFeatured] of featured.entries()) {
      slots += isFeatured ? 2 : 1;
      if (isFeatured) featureGridCount += 1;
      if (slots >= targetSlots && slots % 2 === 0) {
        return { afterIndex: index, linesAbove: hero + slots / 2, featureGridCount };
      }
    }
    return featured.length
      ? { afterIndex: featured.length - 1, linesAbove: hero + Math.ceil(slots / 2), featureGridCount }
      : null;
  }

  for (const [index, isFeatured] of featured.entries()) {
    if (isFeatured) featureGridCount += 1;
    if (index + 1 >= gridLinesTarget) {
      return { afterIndex: index, linesAbove: hero + index + 1, featureGridCount };
    }
  }
  return featured.length
    ? { afterIndex: featured.length - 1, linesAbove: hero + featured.length, featureGridCount }
    : null;
}

/**
 * Settles the first time the ongoing tab is on screen, which is when the block is first placed.
 * The grid announces its tab once it upgrades, and mirrors it on the element for a later caller.
 * @param {HTMLElement} grid
 * @returns {Promise<void>}
 */
function whenOngoingTab(grid) {
  if (grid.dataset.activeTab === 'ongoing') return Promise.resolve();

  return new Promise((resolve) => {
    const controller = new AbortController();
    grid.addEventListener(
      'operation-grid:tab-change',
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail.tab !== 'ongoing') return;
        controller.abort();
        resolve();
      },
      { signal: controller.signal }
    );
  });
}

/** Places the Bestsellers block in the homepage sales grid, on the ongoing tab only. */
class BestsellersPlacement {
  /** @type {HTMLElement} */
  #grid;

  /** @type {HTMLTemplateElement} */
  #template;

  /** @type {HTMLElement | null} */
  #block = null;

  /**
   * @param {HTMLElement} grid - the operation-grid element
   * @param {HTMLTemplateElement} template
   */
  constructor(grid, template) {
    this.#grid = grid;
    this.#template = template;
  }

  /** Follows tab changes and breakpoint crossings, and renders the current tab if the grid already announced one. */
  start() {
    const renderActiveTab = () => this.#render(this.#grid.dataset.activeTab);

    this.#grid.addEventListener('operation-grid:tab-change', (event) => {
      this.#render(/** @type {CustomEvent} */ (event).detail.tab);
    });
    mediaQueryLarge.addEventListener('change', renderActiveTab);
    mediaQueryDesktop.addEventListener('change', renderActiveTab);

    if (this.#grid.dataset.activeTab) renderActiveTab();
  }

  /** @param {string | undefined} tab */
  #render(tab) {
    if (tab !== 'ongoing') {
      if (this.#block) this.#block.hidden = true;
      return;
    }

    const position = resolvePosition(this.#grid);
    if (position === NO_BESTSELLERS) {
      this.#hide('no_bestsellers');
      return;
    }

    const cards = [...this.#grid.querySelectorAll('.operation-grid__card[data-status="active"][data-visible]')];
    const heroCount = Number(this.#grid.dataset.heroCount) || 0;
    const placement = computeInsertion(
      cards.map((card) => card.hasAttribute('data-featured-grid')),
      {
        heroLayout: this.#grid.dataset.heroLayout ?? 'none',
        heroCount,
        isLarge: mediaQueryLarge.matches,
        position,
      }
    );

    if (!placement) {
      this.#hide('no_slot');
      return;
    }

    const detail = {
      blockPosition: placement.linesAbove,
      salesAboveBlock: heroCount + placement.afterIndex + 1,
      featureGridCount: placement.featureGridCount,
    };
    // afterIndex -1 means "right after the hero", so the anchor is the first card and the block goes
    // before it rather than after a card.
    const target = placement.afterIndex === -1 ? null : cards[placement.afterIndex];

    // Same spot: keep the carousel as it is and only refresh the values the tracking reads.
    const anchoredInPlace = target
      ? target.nextElementSibling === this.#block
      : cards[0]?.previousElementSibling === this.#block;
    if (this.#block && anchoredInPlace) {
      this.#applyDetail(this.#block, detail);
      this.#block.hidden = false;
      return;
    }

    // Moving the carousel would reconnect the Horizon slideshow mid-life, so it is rebuilt instead.
    this.#block?.remove();
    this.#block = null;

    const fragment = /** @type {DocumentFragment} */ (this.#template.content.cloneNode(true));
    const block = fragment.querySelector('.bestsellers-block');
    if (!(block instanceof HTMLElement)) {
      this.#hide('fetch_error');
      return;
    }

    this.#applyDetail(block, detail);
    if (target) target.after(block);
    else cards[0].before(block);
    this.#block = block;
    this.#grid.closest('section')?.removeAttribute('data-bestsellers-hidden');
    this.#grid.dispatchEvent(new CustomEvent('bestsellers:inserted', { bubbles: true, detail }));
  }

  /**
   * @param {HTMLElement} block
   * @param {Record<string, string | number>} detail
   */
  #applyDetail(block, detail) {
    for (const [key, value] of Object.entries(detail)) block.dataset[key] = String(value);
  }

  /** @param {'no_slot' | 'fetch_error' | 'no_bestsellers'} reason */
  #hide(reason) {
    if (this.#block) this.#block.hidden = true;

    const section = this.#grid.closest('section');
    if (section?.dataset.bestsellersHidden === reason) return;

    section?.setAttribute('data-bestsellers-hidden', reason);
    this.#grid.dispatchEvent(new CustomEvent('bestsellers:hidden', { bubbles: true, detail: { reason } }));
  }
}

const grid = document.querySelector('operation-grid[data-is-homepage="true"]');
const template = document.getElementById('bestsellers-block');

if (grid instanceof HTMLElement && template instanceof HTMLTemplateElement) {
  whenOngoingTab(grid).then(() => new BestsellersPlacement(grid, template).start());
}
